import React, { useState } from 'react';
import { Link } from 'react-router';
import { Card, Col, Row, Button, ProgressBar, Table, OverlayTrigger, Tooltip, Form, Placeholder } from 'react-bootstrap';
import AdminLayout from '../../../layouts/AdminLayout';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart
} from 'recharts';
import {
  IconBan, IconTrendingDown, IconTrendingUp,
  IconInfoCircle, IconFileInvoice, IconTicket
} from '@tabler/icons-react';
import { toast } from 'sonner';

import { useAuth } from '../../../auth/context/AuthProvider';
import useDashboardTopStats from '../../../services/dashboard-v2/hooks/useDashboardTopStats';
import useDashboardMonthlyPerformance from '../../../services/dashboard-v2/hooks/useDashboardMonthlyPerformance';
import useDashboardRecentBookings from '../../../services/dashboard-v2/hooks/useDashboardRecentBookings';
import useDashboardBookingSources from '../../../services/dashboard-v2/hooks/useDashboardBookingSources';
import useDashboardHousekeeping from '../../../services/dashboard-v2/hooks/useDashboardHousekeeping';
import useDashboardActivities from '../../../services/dashboard-v2/hooks/useDashboardActivities';
import useOccupancyDaily from '../../../services/occupancy/hooks/useOccupancyDaily';
import CreateReservation from '../../pms-reservation/components/CreateReservation';
import NeedsAttentionWidget from '../components/NeedsAttentionWidget';
import NeedsAttentionModal from '../components/NeedsAttentionModal';
import FixIncompleteBookingModal from '../../pms-reservation/components/fix-incomplete-booking/FixIncompleteBookingModal';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';

const cardStyle = {
  border: '1px solid #EAEAEA',
  boxShadow: 'none',
  borderRadius: '8px'
};

const softCardStyle = {
  border: '1px solid #E6F6F2',
  backgroundColor: '#F0FAF6',
  boxShadow: 'none',
  borderRadius: '8px'
};

const getContrastColor = (hexColor: string) => {
  if (!hexColor || !hexColor.startsWith('#')) return '#FFFFFF';

  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

  return yiq >= 160 ? '#000000' : '#FFFFFF';
};

const btnBgColor = '#20C997';

const formatRupiah = (value: number) =>
  'Rp ' + new Intl.NumberFormat('id-ID').format(Math.round(value || 0));

// Custom tooltip untuk chart Monthly Performance: menambah baris Occupancy
// (dari payload BE, rumus sama dengan kartu occupancy) di samping Revenue & ARR.
const MonthlyPerformanceTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  const occupancy = typeof row.occupancy === 'number' ? row.occupancy : 0;

  return (
    <div style={{ background: '#fff', border: '1px solid #EAEAEA', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: 12 }}>
      <div className="fw-bold mb-1" style={{ color: '#333' }}>{row.month_label || row.month}</div>
      <div style={{ color: '#63C2FF' }}>Total Revenue : <span className="fw-bold">{formatRupiah(row.revenue)}</span></div>
      <div style={{ color: '#1B4D3E' }}>Average Room Rate (ARR) : <span className="fw-bold">{formatRupiah(row.arr)}</span></div>
      <div style={{ color: '#20C997' }}>Occupancy : <span className="fw-bold">{occupancy.toFixed(2)}%</span></div>
    </div>
  );
};

const DashboardDesignPage: React.FC = () => {
  const { user } = useAuth();
  const propertyId = user?.active_property_id || '';

  const [activityTab, setActivityTab] = useState<'today_check_in' | 'in_house' | 'today_check_out'>('today_check_in');
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [reservationTab, setReservationTab] = useState<string>('booking');
  const [showNeedsAttentionModal, setShowNeedsAttentionModal] = useState<boolean>(false);
  const [fixModalBookingId, setFixModalBookingId] = useState<string>('');

  const handleFixIt = (bookingId: string) => {
    setShowNeedsAttentionModal(false);
    setFixModalBookingId(bookingId);
  };

  const now = new Date();
  const currentMonth = format(now, 'yyyy-MM');
  const startDate = format(startOfMonth(now), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(now), 'yyyy-MM-dd');
  const [selectedYear, setSelectedYear] = useState(format(now, 'yyyy'));

  // Hooks
  const { data: topStatsRes, isLoading: isLoadingTopStats } = useDashboardTopStats(propertyId);
  const { data: monthlyRes, isLoading: isLoadingMonthly } = useDashboardMonthlyPerformance(propertyId, selectedYear);
  const { data: recentRes, isLoading: isLoadingRecent } = useDashboardRecentBookings(propertyId);
  const { data: sourcesRes, isLoading: isLoadingSources } = useDashboardBookingSources(propertyId, currentMonth);
  const { data: housekeepingRes, isLoading: isLoadingHousekeeping } = useDashboardHousekeeping(propertyId);
  const { data: activitiesRes, isLoading: isLoadingActivities } = useDashboardActivities(propertyId, activityTab);

  const { data: occupancyRes, isFetching: isFetchingOccupancy } = useOccupancyDaily({
    filter: {
      start: startDate,
      end: endDate,
      property_id: propertyId,
      group_by: "date",
    },
    enabled: !!propertyId
  });

  const topStats = topStatsRes?.data;
  const monthlyData = monthlyRes?.data || [];
  const recentBookings = recentRes?.data?.recent_bookings || [];
  const recentSummary = recentRes?.data?.summary;
  const sourcesData = sourcesRes?.data?.sources || [];
  const sourcesTotal = sourcesRes?.data?.total_bookings || 0;
  const housekeeping = housekeepingRes?.data;
  const activitiesList = activitiesRes?.data?.list || [];
  const activitiesSummary = activitiesRes?.data?.summary_counts;

  const rawOccupancyData = occupancyRes as any;
  const formattedOccupancyData = Array.isArray(rawOccupancyData?.data)
    ? rawOccupancyData.data.map((item: any) => ({
      day: item.date ? format(new Date(item.date), 'MM-dd') : '',
      rate: parseFloat(item.occupancy || item.occupancy_rate || item.rate || '0')
    }))
    : Array.isArray(rawOccupancyData)
      ? rawOccupancyData.map((item: any) => ({
        day: item.date ? format(new Date(item.date), 'MM-dd') : '',
        rate: parseFloat(item.occupancy || item.occupancy_rate || item.value || item.rate || '0')
      }))
      : [];

  const renderTrend = (percentage?: number, trend?: 'up' | 'down' | 'neutral') => {
    if (percentage === undefined) return null;
    if (trend === 'up') {
      return (
        <div className="text-success d-flex align-items-center gap-1 mt-2" style={{ fontSize: '11px', fontWeight: 500, color: '#20C997' }}>
          <IconTrendingUp size={14} /> +{percentage}% vs last month
        </div>
      );
    }
    if (trend === 'down') {
      return (
        <div className="text-danger d-flex align-items-center gap-1 mt-2" style={{ fontSize: '11px', fontWeight: 500 }}>
          <IconTrendingDown size={14} /> {percentage}% vs last month
        </div>
      );
    }
    return (
      <div className="text-muted d-flex align-items-center gap-1 mt-2" style={{ fontSize: '11px', fontWeight: 500 }}>
        {percentage}% vs last month
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="container-fluid pt-3 pb-5 position-relative" style={{ backgroundColor: '#F8F9FA', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>

        <NeedsAttentionWidget
          propertyId={propertyId}
          onFixIt={handleFixIt}
          onViewAll={() => setShowNeedsAttentionModal(true)}
        />

        {/* Quick Actions */}
        <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-white" style={{ ...cardStyle }}>
          <div className="d-flex align-items-center gap-4">
            <h6 className="mb-0 fw-bold" style={{ fontSize: '14px' }}>Quick Actions</h6>
            <div className="d-flex gap-3 align-items-center">
              <ReadOnlyGuard>
                <Button
                  onClick={() => { setReservationTab('booking'); setShowCreate(true); }}
                  size="sm"
                  style={{
                    backgroundColor: btnBgColor,
                    borderColor: btnBgColor,
                    color: getContrastColor(btnBgColor),
                    borderRadius: '4px',
                    fontSize: '12px',
                    padding: '4px 12px',
                    fontWeight: 500
                  }}
                >
                  + New Booking
                </Button>
              </ReadOnlyGuard>
              <ReadOnlyGuard>
                <div onClick={() => { setReservationTab('maintenance'); setShowCreate(true); }} className="text-muted d-flex align-items-center gap-1" style={{ fontSize: '12px', cursor: 'pointer' }}>
                  <IconBan size={14} /> Block Room
                </div>
              </ReadOnlyGuard>
            </div>
          </div>

        </div>

        {/* Top Stats */}
        <Row className="mb-3 g-3">
          <Col md={2}>
            <Card style={cardStyle} className="h-100">
              <Card.Body className="p-3 position-relative">
                {isLoadingTopStats ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} className="mb-2 bg-light" />
                    <Placeholder xs={10} size="lg" className="mb-1" />
                    <Placeholder xs={6} size="sm" />
                  </Placeholder>
                ) : (
                  <>
                    <div className="text-muted text-uppercase mb-2" style={{ fontSize: '10px', letterSpacing: '0.5px', fontWeight: 600 }}>Total Revenue</div>
                    <h5 className="mb-1 fw-bold">{topStats?.total_revenue?.formatted || '-'}</h5>
                    {renderTrend(topStats?.total_revenue?.percentage_change, topStats?.total_revenue?.trend)}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card style={cardStyle} className="h-100">
              <Card.Body className="p-3 position-relative">
                {isLoadingTopStats ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} className="mb-2 bg-light" />
                    <Placeholder xs={10} size="lg" className="mb-1" />
                    <Placeholder xs={6} size="sm" />
                  </Placeholder>
                ) : (
                  <>
                    <div className="text-muted text-uppercase mb-2" style={{ fontSize: '10px', letterSpacing: '0.5px', fontWeight: 600 }}>Total Bookings</div>
                    <h5 className="mb-1 fw-bold">{topStats?.total_bookings?.formatted || '-'}</h5>
                    {renderTrend(topStats?.total_bookings?.percentage_change, topStats?.total_bookings?.trend)}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card style={cardStyle} className="h-100">
              <Card.Body className="p-3 position-relative">
                {isLoadingTopStats ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} className="mb-2 bg-light" />
                    <Placeholder xs={10} size="lg" className="mb-1" />
                    <Placeholder xs={6} size="sm" />
                  </Placeholder>
                ) : (
                  <>
                    <div className="text-muted text-uppercase mb-2 d-flex align-items-center gap-1" style={{ fontSize: '10px', letterSpacing: '0.5px', fontWeight: 600 }}>
                      Available Rooms
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-available-rooms">Unsold / Total sellable room-nights this month (Total rooms − Closed − Maintenance). Booked rooms reduce the unsold count.</Tooltip>}>
                        <span style={{ cursor: 'pointer', display: 'inline-flex' }}><IconInfoCircle size={12} className="text-muted opacity-50" /></span>
                      </OverlayTrigger>
                    </div>
                    <h5 className="mb-1 fw-bold">{topStats?.available_rooms?.formatted_ratio || topStats?.available_rooms?.formatted || '-'}</h5>
                    {renderTrend(topStats?.available_rooms?.percentage_change, topStats?.available_rooms?.trend)}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card style={softCardStyle} className="h-100">
              <Card.Body className="p-3 position-relative">
                {isLoadingTopStats ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} className="mb-2 bg-light" />
                    <Placeholder xs={10} size="lg" className="mb-1" />
                    <Placeholder xs={6} size="sm" />
                  </Placeholder>
                ) : (
                  <>
                    <div className="text-muted text-uppercase mb-2 d-flex align-items-center gap-1" style={{ fontSize: '10px', letterSpacing: '0.5px', fontWeight: 600 }}>
                      RevPAR
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-revpar">Room Revenue Divided by Available Rooms</Tooltip>}>
                        <span style={{ cursor: 'pointer', display: 'inline-flex' }}><IconInfoCircle size={12} className="text-muted opacity-50" /></span>
                      </OverlayTrigger>
                    </div>
                    <h5 className="mb-1 fw-bold">{topStats?.revpar?.formatted || '-'}</h5>
                    <div className="text-muted mt-2" style={{ fontSize: '11px' }}>{topStats?.revpar?.subtitle || 'Mtd avg. performance'}</div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card style={softCardStyle} className="h-100">
              <Card.Body className="p-3 position-relative">
                {isLoadingTopStats ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} className="mb-2 bg-light" />
                    <Placeholder xs={10} size="lg" className="mb-1" />
                    <Placeholder xs={6} size="sm" />
                  </Placeholder>
                ) : (
                  <>
                    <div className="text-muted text-uppercase mb-2 d-flex align-items-center gap-1" style={{ fontSize: '10px', letterSpacing: '0.5px', fontWeight: 600 }}>
                      ARR
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-arr">Room Revenue Divided by Rooms Sold</Tooltip>}>
                        <span style={{ cursor: 'pointer', display: 'inline-flex' }}><IconInfoCircle size={12} className="text-muted opacity-50" /></span>
                      </OverlayTrigger>
                    </div>
                    <h5 className="mb-1 fw-bold">{topStats?.arr?.formatted || '-'}</h5>
                    {renderTrend(topStats?.arr?.percentage_change, topStats?.arr?.trend)}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card style={softCardStyle} className="h-100">
              <Card.Body className="p-3 position-relative">
                {isLoadingTopStats ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} className="mb-2 bg-light" />
                    <Placeholder xs={10} size="lg" className="mb-1" />
                    <Placeholder xs={12} size="sm" className="mt-2" />
                  </Placeholder>
                ) : (
                  <>
                    <div className="text-muted text-uppercase mb-2 d-flex align-items-center gap-1" style={{ fontSize: '10px', letterSpacing: '0.5px', fontWeight: 600 }}>
                      Occupancy monthly
                      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-mtd">Total occupied unit to date / total available units to date</Tooltip>}>
                        <span style={{ cursor: 'pointer', display: 'inline-flex' }}><IconInfoCircle size={12} className="text-muted opacity-50" /></span>
                      </OverlayTrigger>
                    </div>
                    <h5 className="mb-2 fw-bold">{topStats?.mtd_occupancy?.formatted || '-'}</h5>
                    <ProgressBar now={topStats?.mtd_occupancy?.value || 0} style={{ height: '4px', backgroundColor: '#EAEAEA' }} variant="success" className="mt-3 rounded-pill" />
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Middle Row */}
        <Row className="mb-3 g-3">
          <Col md={8}>
            <Card style={cardStyle} className="h-100">
              <Card.Body className="p-4 position-relative">
                {isLoadingMonthly ? (
                  <Placeholder as="div" animation="glow" className="d-flex flex-column h-100">
                    <div className="d-flex justify-content-between mb-4">
                      <div>
                        <Placeholder xs={6} className="mb-1" />
                        <Placeholder xs={10} size="sm" />
                      </div>
                      <Placeholder xs={2} size="sm" />
                    </div>
                    <Placeholder xs={12} style={{ height: '280px' }} className="rounded" />
                  </Placeholder>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <h6 className="mb-1 fw-bold" style={{ fontSize: '14px' }}>Monthly Performance</h6>
                        <div className="text-muted" style={{ fontSize: '11px' }}>Revenue (Bar) vs Average Room Rate (Line)</div>
                      </div>
                      <div className="d-flex align-items-center text-muted" style={{ fontSize: '12px' }}>
                        <Form.Select
                          size="sm"
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                          onPointerDown={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="border-0 bg-transparent shadow-none fw-bold text-secondary pe-4"
                          style={{ fontSize: '12px', cursor: 'pointer', width: 'auto' }}
                        >
                          <option value="2027">2027</option>
                          <option value="2026">2026</option>
                          <option value="2025">2025</option>
                          <option value="2024">2024</option>
                        </Form.Select>
                      </div>
                    </div>
                    <div style={{ height: '280px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={monthlyData} margin={{ top: 10, right: 0, bottom: 20, left: -20 }}>
                          <CartesianGrid stroke="#F0F0F0" vertical={false} />
                          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 10 }} dy={10} />
                          <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 10 }} tickFormatter={(val) => `${val / 1000000}M`} />
                          <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: '#999', fontSize: 10 }} tickFormatter={(val) => `${val / 1000}K`} />
                          {/* Sumbu occupancy tersembunyi (persen 0-100) agar skalanya tidak tercampur dgn Revenue/ARR */}
                          <YAxis yAxisId="occupancy" orientation="right" domain={[0, 100]} hide />
                          <RechartsTooltip cursor={{ fill: '#F8F9FA' }} content={<MonthlyPerformanceTooltip />} />
                          <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: '11px', paddingTop: '20px' }} />
                          <Bar yAxisId="left" dataKey="revenue" barSize={70} fill="#63C2FF" name="Total Revenue" />
                          <Line yAxisId="right" type="monotone" dataKey="arr" stroke="#1B4D3E" strokeWidth={2} name="Average Room Rate (ARR)" dot={{ r: 4, fill: '#fff', stroke: '#1B4D3E', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                          <Line yAxisId="occupancy" type="monotone" dataKey="occupancy" stroke="#20C997" strokeWidth={2} strokeDasharray="5 4" name="Occupancy (%)" dot={{ r: 3, fill: '#fff', stroke: '#20C997', strokeWidth: 2 }} activeDot={{ r: 5 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card style={cardStyle} className="h-100">
              <Card.Body className="p-4 d-flex flex-column position-relative">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="mb-0 fw-bold" style={{ fontSize: '14px' }}>Last Received Bookings</h6>
                  <Link to="/admin/reservation" className="text-decoration-none" style={{ fontSize: '11px', color: '#20C997', fontWeight: 500 }}>View All</Link>
                </div>
                {isLoadingRecent ? (
                  <Placeholder as="div" animation="glow" className="d-flex flex-column gap-3 mb-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="d-flex gap-3 align-items-center">
                        <Placeholder className="rounded" style={{ width: '40px', height: '40px' }} />
                        <div className="flex-grow-1">
                          <Placeholder xs={5} className="mb-1" />
                          <Placeholder xs={8} size="sm" className="mb-1 d-block" />
                          <Placeholder xs={3} size="sm" />
                        </div>
                        <div className="text-end">
                          <Placeholder xs={12} style={{ width: '50px' }} className="mb-1" />
                          <Placeholder xs={12} size="sm" style={{ width: '70px' }} />
                        </div>
                      </div>
                    ))}
                  </Placeholder>
                ) : (
                  <>
                    {recentBookings.length > 0 ? (
                      <div className="d-flex flex-column gap-3 flex-grow-1 mt-1 overflow-auto" style={{ maxHeight: '200px' }}>
                        {recentBookings.map((booking: any) => {
                          const roomDetails = `${booking.room_type || 'Unknown Room'} • ${booking.nights || 0} Nights`;
                          return (
                            <div key={booking.id} className="d-flex justify-content-between align-items-center mt-2">
                              <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
                                <div className="d-flex justify-content-center align-items-center fw-bold flex-shrink-0" style={{ width: '40px', height: '40px', backgroundColor: '#E3F2FD', color: '#1976D2', borderRadius: '6px', fontSize: '14px' }}>
                                  {booking.guest_initials || '?'}
                                </div>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                    <div className="fw-bold text-truncate" style={{ fontSize: '13px', color: '#333' }}>{booking.guest_name}</div>

                                    {booking.invoice_number && (
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-inv-${booking.id}`}>Invoice: {booking.invoice_number}</Tooltip>}
                                      >
                                        <div
                                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                          onClick={() => {
                                            navigator.clipboard.writeText(booking.invoice_number!);
                                            toast.success("Invoice copied!");
                                          }}
                                        >
                                          <IconFileInvoice size={14} className="text-primary" />
                                        </div>
                                      </OverlayTrigger>
                                    )}

                                    {booking.voucher_number && (
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-vou-${booking.id}`}>Voucher: {booking.voucher_number}</Tooltip>}
                                      >
                                        <div
                                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                          onClick={() => {
                                            navigator.clipboard.writeText(booking.voucher_number!);
                                            toast.success("Voucher copied!");
                                          }}
                                        >
                                          <IconTicket size={14} className="text-info" />
                                        </div>
                                      </OverlayTrigger>
                                    )}
                                  </div>
                                  <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-room-${booking.id}`}>{roomDetails}</Tooltip>}>
                                    <div className="text-truncate text-muted mb-1 d-block" style={{ fontSize: '11px', maxWidth: '180px', cursor: 'pointer' }}>
                                      {roomDetails}
                                    </div>
                                  </OverlayTrigger>
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>{booking.status}</span>
                                    <span className="text-muted" style={{ fontSize: '10px' }}>{booking.created_at}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-end flex-shrink-0 ms-2">
                                <div className="fw-bold" style={{ fontSize: '13px', color: '#333' }}>{booking.total_price_formatted}</div>
                                <div className="text-muted" style={{ fontSize: '10px' }}>{booking.date_range_formatted}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4 flex-grow-1 text-muted d-flex flex-column justify-content-center align-items-center">
                        <IconBan size={24} stroke={1} color="#E0E0E0" className="mb-2" />
                        <span style={{ fontSize: '12px' }}>No recent bookings</span>
                      </div>
                    )}
                  </>
                )}

                <hr className="my-4" style={{ borderColor: '#EAEAEA' }} />
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="text-muted text-uppercase mb-1" style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.5px' }}>AVG LENGTH OF STAY</div>
                    <div className="fw-bold" style={{ color: '#333', fontSize: '13px' }}>{recentSummary?.avg_length_of_stay_nights || 0} Nights</div>
                  </div>
                  <div>
                    <div className="text-muted text-uppercase mb-1" style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.5px' }}>AVG LEAD TIME</div>
                    <div className="fw-bold" style={{ color: '#333', fontSize: '13px' }}>{recentSummary?.avg_lead_time_days || 0} Days</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Bottom Row */}
        <Row className="mb-3 g-3">
          <Col md={8}>
            <Card style={cardStyle} className="h-100">
              <Card.Body className="p-4 position-relative">
                {isFetchingOccupancy ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={5} className="mb-4" />
                    <Placeholder xs={12} style={{ height: '260px' }} className="rounded" />
                  </Placeholder>
                ) : (
                  <>
                    <h6 className="mb-4 fw-bold" style={{ fontSize: '14px' }}>Daily Occupancy Rate (%) - {format(now, 'MMMM yyyy')}</h6>
                    <div style={{ height: '260px', marginLeft: '-20px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={formattedOccupancyData} margin={{ top: 10, right: 10, bottom: 20, left: 0 }}>
                          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#EAEAEA" />
                          <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#999' }} axisLine={false} tickLine={false} angle={-45} textAnchor="end" dy={10} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#999' }} ticks={[0, 25, 50, 75, 100]} />
                          {/* Tanpa formatter, Recharts memakai nama dataKey mentah dan
                              nilai tanpa satuan ("rate : 100") — menyulitkan user karena
                              tidak jelas itu persen atau jumlah kamar. Nilainya tidak
                              perlu dibulatkan lagi di sini: backend sudah mengirim
                              occupancy dalam format 2 desimal tetap (number_format(round(
                              $occupancy, 2), 2)), jadi 100 tetap "100%" dan 93.33 tetap
                              "93.33%" setelah parseFloat. */}
                          <RechartsTooltip
                            cursor={{ fill: '#F8F9FA' }}
                            formatter={(value) => [`${value}%`, 'occ rate']}
                          />
                          <Bar dataKey="rate" fill="#42A5F5" barSize={10} radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <div className="d-flex flex-column h-100 gap-3">
              <Card style={cardStyle} className="flex-grow-1">
                <Card.Body className="p-4 position-relative">
                  <h6 className="mb-2 fw-bold" style={{ fontSize: '14px' }}>Source of Booking</h6>
                  {isLoadingSources ? (
                    <Placeholder as="div" animation="glow" className="mt-3">
                      <div className="d-flex justify-content-center mb-3">
                        <Placeholder className="rounded-circle" style={{ width: '150px', height: '150px' }} />
                      </div>
                      <div className="d-flex justify-content-between px-3">
                        <Placeholder xs={4} />
                        <Placeholder xs={4} />
                      </div>
                    </Placeholder>
                  ) : (
                    <>
                      {sourcesData.length > 0 ? (
                        <>
                          <div className="d-flex position-relative align-items-center justify-content-center" style={{ height: '200px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={sourcesData} innerRadius={60} outerRadius={85} paddingAngle={0} dataKey="count" nameKey="name" stroke="none">
                                  {sourcesData.map((entry: any, index: number) => (
                                    <Cell key={`cell-${index}`} fill={entry.color_hex || '#CCC'} />
                                  ))}
                                </Pie>
                                <RechartsTooltip />
                              </PieChart>
                            </ResponsiveContainer>
                            <div className="position-absolute text-center" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                              <div className="fw-bold" style={{ color: '#333', fontSize: '20px' }}>{sourcesTotal}</div>
                              <div className="text-muted" style={{ fontSize: '10px' }}>Total</div>
                            </div>
                          </div>
                          <div className="d-flex flex-wrap justify-content-between px-3 mt-2" style={{ fontSize: '10px' }}>
                            {sourcesData.map((src: any, i: number) => (
                              <div key={i} className="w-50 mb-3 d-flex align-items-center gap-2">
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: src.color_hex || '#CCC' }}></div>
                                <span className="text-muted">{src.name} ({src.percentage}%)</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="d-flex justify-content-center align-items-center flex-column h-100 text-muted">
                          <IconBan size={24} stroke={1} color="#E0E0E0" className="mb-2" />
                          <span style={{ fontSize: '12px' }}>No source data</span>
                        </div>
                      )}
                    </>)}
                </Card.Body>
              </Card>
              <Card style={cardStyle}>
                <Card.Body className="p-3 position-relative">
                  <h6 className="mb-3 fw-bold" style={{ fontSize: '13px' }}>Housekeeping Status</h6>
                  {isLoadingHousekeeping ? (
                    <Placeholder as="div" animation="glow" className="d-flex justify-content-between gap-2">
                      <Placeholder className="flex-fill rounded py-4" />
                      <Placeholder className="flex-fill rounded py-4" />
                      <Placeholder className="flex-fill rounded py-4" />
                    </Placeholder>
                  ) : (
                    <div className="d-flex justify-content-between text-center gap-2">
                      <div className="flex-fill py-2 rounded" style={{ border: '1px solid #EAEAEA', backgroundColor: '#FFF' }}>
                        <div className="fw-bold fs-5 text-dark">{housekeeping?.clean || 0}</div>
                        <div className="text-muted mt-1" style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.5px' }}>CLEAN</div>
                      </div>
                      <div className="flex-fill py-2 rounded" style={{ backgroundColor: '#FFEBEE' }}>
                        <div className="fw-bold fs-5 text-danger">{housekeeping?.dirty || 0}</div>
                        <div className="text-danger mt-1" style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.5px' }}>DIRTY</div>
                      </div>
                      <div className="flex-fill py-2 rounded" style={{ backgroundColor: '#FFF8E1' }}>
                        <div className="fw-bold fs-5" style={{ color: '#F57F17' }}>{housekeeping?.maintenance || 0}</div>
                        <div className="mt-1" style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.5px', color: '#F57F17' }}>MAINT.</div>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>

        {/* Activities Table */}
        <Card style={cardStyle} className="mt-2 position-relative">
          <Card.Body className="p-0">
            <div className="d-flex gap-4 border-bottom px-4 pt-3" style={{ borderColor: '#EAEAEA' }}>
              <div
                onClick={() => setActivityTab('today_check_in')}
                className="pb-2 fw-bold d-flex align-items-center"
                style={{ cursor: 'pointer', fontSize: '13px', color: activityTab === 'today_check_in' ? '#20C997' : '#6c757d', borderBottom: activityTab === 'today_check_in' ? '2px solid #20C997' : 'none' }}>
                <span>Today Check-In</span>
                <span className="rounded-circle ms-2 d-flex justify-content-center align-items-center" style={{ backgroundColor: activityTab === 'today_check_in' ? '#E8F5E9' : '#F8F9FA', color: activityTab === 'today_check_in' ? '#2E7D32' : '#6c757d', fontSize: '10px', width: '20px', height: '20px', fontWeight: 600 }}>{activitiesSummary?.today_check_in || 0}</span>
              </div>
              <div
                onClick={() => setActivityTab('in_house')}
                className="pb-2 fw-bold d-flex align-items-center"
                style={{ cursor: 'pointer', fontSize: '13px', color: activityTab === 'in_house' ? '#20C997' : '#6c757d', borderBottom: activityTab === 'in_house' ? '2px solid #20C997' : 'none' }}>
                <span>In House</span>
                <span className="rounded-circle ms-2 d-flex justify-content-center align-items-center" style={{ backgroundColor: activityTab === 'in_house' ? '#E8F5E9' : '#F8F9FA', color: activityTab === 'in_house' ? '#2E7D32' : '#6c757d', fontSize: '10px', width: '20px', height: '20px', fontWeight: 600 }}>{activitiesSummary?.in_house || 0}</span>
              </div>
              <div
                onClick={() => setActivityTab('today_check_out')}
                className="pb-2 fw-bold d-flex align-items-center"
                style={{ cursor: 'pointer', fontSize: '13px', color: activityTab === 'today_check_out' ? '#20C997' : '#6c757d', borderBottom: activityTab === 'today_check_out' ? '2px solid #20C997' : 'none' }}>
                <span>Today Check-Out</span>
                <span className="rounded-circle ms-2 d-flex justify-content-center align-items-center" style={{ backgroundColor: activityTab === 'today_check_out' ? '#E8F5E9' : '#F8F9FA', color: activityTab === 'today_check_out' ? '#2E7D32' : '#6c757d', fontSize: '10px', width: '20px', height: '20px', fontWeight: 600 }}>{activitiesSummary?.today_check_out || 0}</span>
              </div>
            </div>

            <div className="px-0 py-2">
              {isLoadingActivities ? (
                <Placeholder as="div" animation="glow" className="p-4">
                  <Placeholder xs={12} size="lg" className="mb-3" />
                  <Placeholder xs={12} className="mb-2" />
                  <Placeholder xs={12} className="mb-2" />
                  <Placeholder xs={12} className="mb-2" />
                </Placeholder>
              ) : (
                <>
                  <Table borderless responsive style={{ fontSize: '11px', margin: 0 }}>
                    <thead>
                      <tr className="text-muted" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        <th className="fw-normal py-3 px-4" style={{ letterSpacing: '0.5px' }}>RESERVATION</th>
                        <th className="fw-normal py-3 px-4" style={{ letterSpacing: '0.5px' }}>NAME</th>
                        <th className="fw-normal py-3 px-4" style={{ letterSpacing: '0.5px' }}>ROOMS</th>
                        <th className="fw-normal py-3 px-4" style={{ letterSpacing: '0.5px' }}>CHECK-IN</th>
                        <th className="fw-normal py-3 px-4" style={{ letterSpacing: '0.5px' }}>CHECK-OUT</th>
                        <th className="fw-normal py-3 px-4" style={{ letterSpacing: '0.5px' }}>STATUS</th>
                      </tr>
                    </thead>
                    {activitiesList.length > 0 && (
                      <tbody>
                        {activitiesList.map((act: any) => (
                          <tr key={act.booking_id} style={{ borderBottom: '1px solid #F8F9FA' }}>
                            <td className="py-3 px-4 fw-bold">{act.invoice_number || '-'}</td>
                            <td className="py-3 px-4 text-dark">{act.guest_name}</td>
                            <td className="py-3 px-4 text-muted">{act.rooms_info}</td>
                            <td className="py-3 px-4 text-muted">{act.check_in}</td>
                            <td className="py-3 px-4 text-muted">{act.check_out}</td>
                            <td className="py-3 px-4">
                              <span style={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontSize: '9px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>{act.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    )}
                  </Table>

                  {activitiesList.length === 0 && !isLoadingActivities && (
                    <div className="text-center py-5">
                      <div className="mb-3 text-muted">
                        <IconBan size={48} stroke={1} color="#E0E0E0" />
                      </div>
                      <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: '14px' }}>No data available</h6>
                      <p className="text-muted mb-4" style={{ fontSize: '12px' }}>There is no activity data for this selection.</p>
                    </div>
                  )}
                </>)}
            </div>
          </Card.Body>
        </Card>

        <ReadOnlyGuard mode="hide">
          <CreateReservation show={showCreate} onClose={() => setShowCreate(false)} initialTab={reservationTab} />
        </ReadOnlyGuard>
        <NeedsAttentionModal
          show={showNeedsAttentionModal}
          propertyId={propertyId}
          onClose={() => setShowNeedsAttentionModal(false)}
          onFixIt={handleFixIt}
        />
        <FixIncompleteBookingModal
          show={!!fixModalBookingId}
          bookingRoomId={fixModalBookingId}
          onClose={() => setFixModalBookingId('')}
        />
      </div>
    </AdminLayout>
  );
};

export default DashboardDesignPage;
